import React, { Dispatch, SetStateAction } from 'react';

import { ViewModelForUIBinding } from './UIBinding';

/**
 * Setups up the UI bindings for React functional component.
 * It goes over each of the decorated property and calls "setState" function in the UI update notification function.
 * This function should be called directly inside the function of the React functional component since it will invoke React.useState().
 * The property value handler will be called after the property value is set and "setState" is called.
 *
 * Usage:
 * function Omnibox(props: OmniboxOwnProps): JSX.Element {
 *   ...  make sure the view model instance is created already.
 *   setupUIBindingForReactFunctionalComponent(viewModel);
 *   ... other code ...
 *   return <div>Hello.</div>; *
 * }
 */
export function setupUIBindingForReactFunctionalComponent(viewModel: IViewModelWithReactLifecycleHooks) {
    processViewModelBindingProperties(viewModel);
    setupReactComponentLifecycleHooks(viewModel);
}

export interface IViewModelWithReactLifecycleHooks {
    onMount(): void;
    onUnmount(): void;
}

function setupReactComponentLifecycleHooks(viewModel: IViewModelWithReactLifecycleHooks) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(
        () => {
            if (viewModel.onMount) {
                viewModel.onMount();
            }

            return () => {
                processViewModelBindingProperties(viewModel, true);

                if (viewModel.onUnmount) {
                    viewModel.onUnmount();
                }
            };
        },
        [viewModel]
    );
}

function processViewModelBindingProperties(viewModel: IViewModelWithReactLifecycleHooks, skipReactSetState?: boolean) {
    const viewModelForUIBinding = viewModel as unknown as ViewModelForUIBinding;
    for (const property in viewModelForUIBinding.__uiUpdateNotificationMap) {
        ((currentProperty) => {
            let propertyValue = viewModel[currentProperty];

            //@ts-ignore TS6133
            let state: any | undefined = propertyValue;
            let setState: Dispatch<SetStateAction<unknown>> = undefined;

            if (!skipReactSetState) {
                [state, setState] = React.useState(propertyValue);
            }

            const getter = () => {
                return propertyValue;
            };
            const setter = (newValue: any) => {
                propertyValue = newValue;

                if (setState) {
                    setState(newValue);
                }

                const propertyUpdatedHandler = viewModelForUIBinding.__uiUpdateNotificationMap[currentProperty];
                if (propertyUpdatedHandler) {
                    propertyUpdatedHandler(viewModel, newValue);
                }
            };

            Object.defineProperty(viewModel, currentProperty, {
                get: getter,
                set: setter,
                enumerable: true,
                configurable: true,
            });
        })(property);
    }
}